import { ReactNode, useState } from 'react';
import {
  Avatar,
  Badge,
  Card,
  RefreshButton,
  Text,
  cx,
  typography,
} from '../../components/ui';
import { getStoredFunMode, translate } from '../i18n';

export interface WorkTab {
  id: string;
  icon: ReactNode;
  title: string;
  /** Live-count line under the title, e.g. `27 entities`. */
  subtitle: string;
  content: ReactNode;
}

const tabBase =
  'group/tab appearance-none bg-transparent flex items-center gap-3 min-w-0 p-3 text-left rounded-xl border border-transparent outline-none cursor-pointer transition ' +
  'hover:bg-muted-strong data-[focus-visible]:ring-2 focus-visible:ring-4 focus-visible:ring-[rgb(var(--c-ring)/0.24)]';
const tabActive = 'border-brand-solid/30 bg-brand-solid/10';
const chipBase =
  'flex items-center justify-center size-9 shrink-0 rounded-lg border border-default text-fg-tertiary transition-colors';
const chipActive = 'border-brand-solid/40 bg-brand-solid/15 text-brand-text';

/**
 * The Home page's single work card: a tab bar header — each tab a square icon
 * chip + title + live-count subtitle, the active one brand-tinted — swapping
 * the table rendered in the card body. Built on the design-system Card.
 *
 * Uncontrolled by default; pass `activeId` + `onActiveChange` to drive the
 * active tab from the outside (e.g. URL state, so reload restores it).
 */
export const WorkTabsCard = ({
  tabs,
  activeId: controlledId,
  onActiveChange,
}: {
  tabs: WorkTab[];
  activeId?: string;
  onActiveChange?: (id: string) => void;
}) => {
  const [internalId, setInternalId] = useState(tabs[0]?.id);
  const activeId = controlledId ?? internalId;
  const active = tabs.find(tab => tab.id === activeId) ?? tabs[0];
  const select = (id: string) =>
    onActiveChange ? onActiveChange(id) : setInternalId(id);

  return (
    <Card flush>
      {/* TODO(a11y): full tablist keyboard nav (arrow keys + roving
          tabindex) — follow-up, see UI review finding #19. */}
      <div
        role="tablist"
        className="grid gap-1 border-b border-default bg-muted/50 p-1 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]"
      >
        {tabs.map(tab => {
          const isActive = tab.id === active?.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={cx(tabBase, isActive && tabActive)}
              onClick={() => select(tab.id)}
            >
              <span className={cx(chipBase, isActive && chipActive)}>
                {tab.icon}
              </span>
              <span className="min-w-0">
                <Text
                  variant="body"
                  as="span"
                  className="block truncate font-medium"
                >
                  {tab.title}
                </Text>
                <Text
                  variant="caption"
                  tone="tertiary"
                  as="span"
                  className="block truncate"
                >
                  {tab.subtitle}
                </Text>
              </span>
            </button>
          );
        })}
      </div>
      <div role="tabpanel">{active?.content}</div>
    </Card>
  );
};

/** Section header inside a tab panel: title + brand count pill + description,
 * with an optional right-aligned controls slot (e.g. a filter). */
export const PanelHeader = (props: {
  title: string;
  /** Count pill text, e.g. `27 entities` — omitted while loading. */
  count?: string;
  description?: string;
  /** Right-aligned controls (filters, toggles). */
  actions?: ReactNode;
}) => (
  <div className="flex flex-wrap items-start justify-between gap-3 px-5 pt-4 pb-3">
    <div className="min-w-0">
      <div className="flex items-center gap-2">
        <Text variant="title" as="h2" className="text-base">
          {props.title}
        </Text>
        {props.count !== undefined && (
          <Badge color="brand">{props.count}</Badge>
        )}
      </div>
      {props.description && (
        <Text variant="body" tone="tertiary" className="mt-1">
          {props.description}
        </Text>
      )}
    </div>
    {props.actions && (
      <div className="flex shrink-0 items-center gap-2">{props.actions}</div>
    )}
  </div>
);

/**
 * Compose a panel header's right-aligned controls: the optional section owner
 * filter plus the per-panel Refresh button. Every Home panel gets its own
 * Refresh (a hard cache-bypass refetch of just that panel) — see the "every
 * element has its own refresh" rule in CLAUDE.md. Returns `undefined` when
 * there's nothing to show so PanelHeader can skip the actions row entirely.
 */
export const panelActions = ({
  ownerFilter,
  onRefresh,
  isRefreshing,
  refreshLabel,
  extra,
}: {
  ownerFilter?: ReactNode;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  /** Panel name for the Refresh tooltip, e.g. `Sentry issues`. */
  refreshLabel: string;
  /** Additional controls rendered before the Refresh button (e.g. filters). */
  extra?: ReactNode;
}): ReactNode => {
  if (!ownerFilter && !onRefresh && !extra) return undefined;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {ownerFilter}
      {extra}
      {onRefresh && (
        <RefreshButton
          onRefresh={onRefresh}
          isDisabled={isRefreshing}
          tooltip={translate('home.panel.refreshTooltip', getStoredFunMode(), {
            label: refreshLabel,
          })}
        />
      )}
    </div>
  );
};

/** Muted single-line state (empty / error / connect hints) inside a panel. */
export const PanelMessage = (props: { children: ReactNode }) => (
  <Text variant="body" tone="tertiary" className="px-5 pt-2 pb-6">
    {props.children}
  </Text>
);

/**
 * Leading row avatar — the author's GitHub avatar when a URL is available,
 * otherwise the seed's initial on a foundation-tinted background (design-system
 * Avatar).
 */
export const RowAvatar = (props: { seed: string; imageUrl?: string }) => (
  <Avatar name={props.seed} src={props.imageUrl} size="sm" />
);

/** Semantic status-dot tone — colors come from the foundation, not the panel. */
export type StatusTone = 'neutral' | 'success' | 'error' | 'warning';

const dotTone: Record<StatusTone, string> = {
  neutral: 'bg-fg-muted',
  success: 'bg-success',
  error: 'bg-error',
  warning: 'bg-warning',
};

/**
 * Hairline status/type pill used in the table rows, with an optional status
 * dot tinted from a foundation semantic `tone`.
 */
export const StatusPill = (props: { label: string; tone?: StatusTone }) => (
  <span
    className={cx(
      typography.caption,
      'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-default bg-surface px-2 py-0.5 text-fg-tertiary',
    )}
  >
    {props.tone && (
      <span
        aria-hidden
        className={cx('size-1.5 shrink-0 rounded-full', dotTone[props.tone])}
      />
    )}
    {props.label}
  </span>
);

/**
 * Shared row layout classes for the Home tab tables (design-system tokens):
 * a hover-highlighted flex row with a primary link, secondary meta and a
 * trailing controls cluster. Kept as a hook so existing panels read
 * `useWorkRowStyles().row` unchanged.
 */
export const useWorkRowStyles = () => ({
  row: 'flex items-center gap-3 px-5 py-3 border-t border-default transition-colors hover:bg-muted',
  text: 'grow min-w-0',
  primary: cx(
    typography.bodyMedium,
    'block truncate text-fg no-underline transition-colors hover:text-brand-text',
  ),
  secondary: cx(typography.caption, 'block truncate text-fg-tertiary'),
  trailing: 'flex items-center gap-3 shrink-0',
});
